import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { api } from "../../api";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { setSwitchRegion } from "../../redux/authSlice";

/* ---------------------------------------
   Get current month start & end
--------------------------------------- */
const getCurrentMonthRange = () => {
  const now = new Date();

  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    1
  ).toLocaleDateString("en-ca");

  const end = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0
  ).toLocaleDateString("en-ca");

  return { start, end };
};

const MasterStockHistory = () => {
  const { masterProductId } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();

  const { start, end } = getCurrentMonthRange();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(start);
  const [toDate, setToDate] = useState(end);
  const [master, setMaster] = useState({})

  /* ---------------------------------------
     Disable region switch
  --------------------------------------- */
  useEffect(() => {
    dispatch(setSwitchRegion(false));
    return () => dispatch(setSwitchRegion(true));
  }, [dispatch]);

  /* ---------------------------------------
     Fetch stock history
  --------------------------------------- */
  const fetchStockHistory = async () => {
    try {
      setLoading(true);

      const response = await api.products.getMasterStockHistory(
        {
          masterProductId,
          fromDate,
          toDate,
        },
        token
      );

      if (!response.success) {
        toast.error("You don't have access to this product");
        navigate("/master");
        return;
      }

      setHistory(response.data?.result || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load stock history");
      navigate("/master");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);


  useEffect(() => {
          api.products.getMasterProductById({ masterProductId: masterProductId },token)
              .then(res => {
                  if (res.success === false) {
                      toast.error(res.message);
                  }
                   setMaster(res.data)
                
              }).catch(err => {
                  toast.error("Something went wrong");
              })
      }, [token,masterProductId])

  /* ---------------------------------------
     DOWNLOAD UPLOAD QTY
  --------------------------------------- */
  const [downloadingQty, setDownloadingQty] = useState(false);

  const handleDownloadUploadQty = async () => {
    try {
      setDownloadingQty(true);

      const response = await api.products.downloadUploadQty(
        { masterProductId, fromDate, toDate },
        token
      );

      if (!response) {
        toast.error("No upload quantity data found for the selected date range");
        return;
      }

      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `upload-qty-${masterProductId}-${fromDate}-to-${toDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Download Upload Qty failed");
    } finally {
      setDownloadingQty(false);
    }
  };

  /* ---------------------------------------
     EXPORT (BACKEND API)
  --------------------------------------- */
  const handleExport = async () => {
    try {
      setLoading(true);

      const payload = {
        masterProductId,
        fromDate,
        toDate,
      };

      const response = await api.products.exportMasterStockHistory(
        payload,
        token
      );

      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `master_stock_history_${fromDate}_to_${toDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------
     Table columns (LEFT aligned)
  --------------------------------------- */
  const leftAlign = {
    justifyContent: "flex-start",
    textAlign: "left",
  };

  const columns = [
    { name: "Date", selector: (row) => row.stockDate, sortable: true, style: leftAlign },
    { name: "Opening", selector: (row) => row.openingStock, style: leftAlign },
    { name: "Stock In", selector: (row) => row.stockIn, style: leftAlign },
    { name: "Stock Out", selector: (row) => row.stockOut, style: leftAlign },
    { name: "Closing", selector: (row) => row.closingStock, style: leftAlign },
  ];

  const customStyles = {
    headCells: {
      style: {
        justifyContent: "flex-start",
        textAlign: "left",
        fontWeight: "600",
      },
    },
    cells: {
      style: {
        justifyContent: "flex-start",
        textAlign: "left",
      },
    },
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Stock History - {master.masterProductName}</h4>

        <div>
          <button
            className="btn btn-primary mr-2"
            onClick={handleDownloadUploadQty}
            disabled={downloadingQty}
          >
            {downloadingQty ? "Downloading..." : "Download Upload Qty"}
          </button>

          <button
            className="btn btn-success mr-2"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? "Exporting..." : "Export"}
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => navigate("/master")}
          >
            Back
          </button>
        </div>
      </div>

      {/* Date filters */}
      <div className="row mb-3">
        <div className="col-md-3">
          <label>From Date</label>
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <label>To Date</label>
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      {/* Data table */}
      <DataTable
        columns={columns}
        data={history}
        progressPending={loading}
        pagination
        highlightOnHover
        persistTableHead
        customStyles={customStyles}
        noDataComponent="No stock history found"
      />
    </div>
  );
};

export default MasterStockHistory;
